/**
 * Publishing the phone's camera to the laptop while the phone is being placed.
 *
 * The student props the phone against a book, pointing away from themselves,
 * and then cannot see its screen. Until now that meant placement was blind:
 * the only confirmation that the phone was aimed at anything useful came when
 * someone reviewed the footage, long after the exam.
 *
 * So during the placement stage only, the phone pushes a small still to the
 * server and the laptop shows it. Deliberately narrow:
 *
 *  - **Only while the phone is being aimed** — from pairing until placement is
 *    confirmed. It starts at pairing rather than at placement because the
 *    phone learns the stage from its heartbeat, up to 15 seconds late: waiting
 *    for the placement stage would leave the laptop showing "waiting for the
 *    phone" for fifteen seconds in the ordinary, everything-working case, and
 *    a good fraction of students would pick the phone up to check it — losing
 *    the position the panel exists to protect. It stops at confirmation, since
 *    nobody is aiming the phone after that and a relay running through the
 *    exam would be a second upload pipeline competing with the recording that
 *    actually matters.
 *  - **Small and lossy.** Downscaled and heavily compressed: this is for
 *    "is it pointing at me and my desk", not for reading anything.
 *  - **Never queued.** If a frame fails or is slow, it is dropped, not
 *    retried. The next one is along in a moment and a stale frame is worse
 *    than no frame. This is the opposite of the chunk uploader, which must
 *    never drop anything, and the difference is the whole point: one is a
 *    record, this is a viewfinder.
 *
 * It shares the camera's existing video element, so it costs one canvas draw
 * per frame and never touches the MediaRecorder.
 */
import { useEffect, useRef, type RefObject } from "react";
import { publishViewfinderFrame } from "@/lib/exam-api";
import { captureStill } from "@/lib/recorder";

/**
 * ~1.4 fps. Fast enough to feel connected to your own hand while nudging the
 * phone, slow enough that a frame is never still uploading when the next one
 * is taken.
 */
const FRAME_INTERVAL_MS = 700;

/**
 * After a failure, wait this long instead.
 *
 * Without a backoff a phone on a saturated link fires a doomed request every
 * 700 ms, holding a connection slot against the chunk uploader — whose first
 * chunk carries the WebM header, and losing it makes every later fragment of
 * that recording undecodable. A viewfinder must never be able to cost a
 * recording.
 */
const BACKOFF_MS = 4000;

/** Long edge in pixels. A 320px frame is ~15 KB and plenty for aiming. */
const FRAME_LONG_EDGE = 360;

const FRAME_QUALITY = 0.5;

export function useViewfinderPublish({
  attemptId,
  deviceToken,
  videoRef,
  enabled,
}: {
  attemptId: string | null;
  deviceToken: string | null;
  // A ref, not the element: the <video> is mounted by the camera and swapped
  // as the stream is (re)acquired, and a ref keeps this hook from restarting
  // its interval every time that happens.
  videoRef: RefObject<HTMLVideoElement | null>;
  enabled: boolean;
}): void {
  // Held in a ref so a slow upload cannot overlap the next tick. Without it a
  // phone on a bad connection builds a backlog of in-flight frames and every
  // one of them lands out of order.
  const busy = useRef(false);

  useEffect(() => {
    if (!enabled || !attemptId || !deviceToken) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      if (cancelled || busy.current) return;

      const video = videoRef.current;
      // No element yet, or the camera has not produced a frame. Nothing to
      // publish — but the loop must still be rescheduled, or this early
      // return kills it permanently. (With an interval this was free; with a
      // self-rescheduling timeout it is the difference between "the first
      // frame is a moment late" and "the panel never shows anything".)
      if (!video) {
        timer = setTimeout(() => void tick(), FRAME_INTERVAL_MS);
        return;
      }

      busy.current = true;
      let failed = false;
      try {
        const blob = await captureStill(video, FRAME_QUALITY, FRAME_LONG_EDGE);
        if (cancelled || !blob) return;
        await publishViewfinderFrame(attemptId, blob, deviceToken);
      } catch {
        failed = true;
        // Deliberately silent. A dropped viewfinder frame is not a problem
        // the student can act on, and the placement screen already reports
        // the real camera and upload state. Surfacing this would train them
        // to ignore warnings that do matter.
      } finally {
        busy.current = false;
        if (!cancelled) {
          timer = setTimeout(
            () => void tick(),
            failed ? BACKOFF_MS : FRAME_INTERVAL_MS,
          );
        }
      }
    };

    // A self-rescheduling timeout rather than an interval, so the backoff
    // above can actually lengthen the gap. An interval would keep firing at
    // its fixed rate no matter how the last attempt went.
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [attemptId, deviceToken, videoRef, enabled]);
}
